import { useId, useState } from "react";
import { userFacingMessage } from "@veto/domain";
import { ModalShell } from "../../components/ModalShell";
import { changePasscode, MIN_PASSCODE_LENGTH } from "../../messaging/accountLifecycle";
import { passcodeStrength } from "../Onboarding";

/**
 * Change passcode (phone parity): the current passcode is checked against the stored wrap,
 * then the same vault key is re-wrapped under the new one. The passcodes live only in this
 * dialog's state and are gone when it closes.
 */
export function ChangePasscodeModal({
  onClose,
  onDone,
  change = changePasscode,
}: {
  onClose: () => void;
  onDone: () => void;
  /** Injectable for tests; defaults to the real vault re-wrap. */
  change?: (current: string, next: string, confirm: string) => Promise<string | null>;
}) {
  const ids = useId();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const strength = passcodeStrength(next);

  async function submit() {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const err = await change(current, next, again);
      if (err) {
        setError(err);
        setBusy(false);
        return;
      }
      setCurrent("");
      setNext("");
      setAgain("");
      onDone();
    } catch (e) {
      setError(userFacingMessage(e));
      setBusy(false);
    }
  }

  return (
    <ModalShell
      title="Change passcode"
      ariaLabel="Change passcode"
      onClose={() => {
        if (!busy) onClose();
      }}
      size="sm"
      className="sv-passcode-card"
      footer={
        <div className="actions-row">
          <button type="button" className="btn secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn"
            form={`${ids}-form`}
            disabled={busy || !current || !next || !again}
          >
            {busy ? "Changing…" : "Change"}
          </button>
        </div>
      }
    >
      <form
        id={`${ids}-form`}
        className="stack sv-passcode-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <p className="muted text-sm" style={{ margin: 0 }}>
          Your messages stay as they are; only the key that protects them is re-wrapped. Chat locks
          keep their own PINs.
        </p>
        <div className="field">
          <label htmlFor={`${ids}-current`}>Current passcode</label>
          <input
            id={`${ids}-current`}
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            disabled={busy}
            autoFocus
          />
        </div>
        <div className="field">
          <label htmlFor={`${ids}-next`}>New passcode</label>
          <input
            id={`${ids}-next`}
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            disabled={busy}
            aria-describedby={`${ids}-hint`}
          />
          <span id={`${ids}-hint`} className="muted text-xs">
            {next ? strength.label : `At least ${MIN_PASSCODE_LENGTH} characters`}
          </span>
        </div>
        <div className="field">
          <label htmlFor={`${ids}-again`}>Repeat new passcode</label>
          <input
            id={`${ids}-again`}
            type="password"
            autoComplete="new-password"
            value={again}
            onChange={(e) => setAgain(e.target.value)}
            disabled={busy}
          />
        </div>
        {error && (
          <p className="sv-form-error" role="alert">
            {error}
          </p>
        )}
      </form>
    </ModalShell>
  );
}
